// src/App.tsx

import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import { User, LogOut, Shield } from 'lucide-react';
import { Toaster } from './components/ui/sonner.tsx';
import { Button } from './components/ui/button.tsx';
// Google OAuth sağlayıcısını import ettik
import { GoogleOAuthProvider } from '@react-oauth/google';

// --- BİLEŞENLER ---
import { HomePage } from './components/HomePage.tsx';
import { MembershipPage } from './components/MembershipPage.tsx';
import { EventsPage } from './components/EventsPage.tsx';
import { NewsPage } from './components/NewsPage.tsx';
import { GalleryPage } from './components/GalleryPage.tsx';
import { AlbumDetailPage } from './components/AlbumDetailPage.tsx';
import { ContactPage } from './components/ContactPage.tsx';
import { AlumniNetworkPage } from './components/AlumniNetworkPage.tsx';
import { ProfilePage } from './components/ProfilePage.tsx';
import { ProfileSettingsPage } from './components/ProfileSettingsPage.tsx';
import { AdminPanelPage } from './components/AdminPanelPage.tsx';
import { Navigation } from './components/Navigation.tsx';
import { AnnouncementDetailPage } from './components/AnnouncementDetailPage.tsx';
import authService from './services/auth';
import logoImage from './assets/logo.png';
import bgImage from './assets/bg.png';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID as string;
export default function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // KİLİT FONKSİYON: Sayfayı yenilemeden giriş durumunu anında günceller
  const triggerLoginState = () => {
    const token = localStorage.getItem('access');
    if (token) {
      setIsLoggedIn(true);
      setIsAdmin(authService.isUserAdmin());
    } else {
      setIsLoggedIn(false);
      setIsAdmin(false);
    }
  };

  useEffect(() => {
    console.log("Google Client ID Değerim:", process.env.REACT_APP_GOOGLE_CLIENT_ID);
    triggerLoginState();
    setIsLoading(false);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    localStorage.removeItem('userId'); 
    setIsLoggedIn(false);
    setIsAdmin(false);
    window.location.href = '/'; 
  };

  if (isLoading) {
    return <div className="flex h-screen items-center justify-center text-slate-500">Yükleniyor...</div>;
  }

  return (
    // Tüm uygulamayı .env dosyasındaki Client ID ile sarmaladık
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ""}>
      <Router>
        <div className="min-h-screen bg-white">
          {/* Header */}
          <header className="bg-white shadow-sm sticky top-0 z-50">
            <div className="container mx-auto px-4 py-4 flex items-center justify-between">
              
              {/* Logo Alanı */}
              <div className="flex items-center gap-3">
                <Link to="/" className="flex items-center gap-3">
                  <img src={logoImage} alt="Logo" className="h-24 w-24" />
                  <div>
                    <h1 className="text-red-700 font-bold text-lg md:text-2xl">Süleyman Demirel Anadolu Lisesi</h1>
                    <p className="text-slate-600 text-3sm">Mezunlar Topluluğu</p>
                  </div>
                </Link>
              </div>

              {/* Sağ Üst Kullanıcı Menüsü */}
              <div className="flex items-center gap-2">
                {isLoggedIn ? (
                  <>
                    {isAdmin && (
                      <Link to="/admin">
                        <Button variant="ghost" size="sm" className="gap-2 text-red-600 font-semibold">
                          <Shield className="h-4 w-4" />
                          <span className="hidden md:inline">Admin Panel</span>
                        </Button>
                      </Link>
                    )}
                    
                    <Link to="/profil">
                      <Button variant="ghost" size="sm" className="gap-2">
                        <User className="h-4 w-4" />
                        <span className="hidden md:inline">Profilim</span>
                      </Button>
                    </Link>

                    <Button variant="ghost" size="sm" onClick={handleLogout} className="gap-2 text-slate-600 hover:text-red-600">
                      <LogOut className="h-4 w-4" />
                      <span className="hidden md:inline">Çıkış</span>
                    </Button>
                  </>
                ) : (
                  <Link to="/uyelik?tab=giris">
                     <Button variant="outline" size="sm" className="gap-2 border-red-600 text-red-600 hover:bg-red-50">
                      <User className="h-4 w-4" />
                      Üye Girişi
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </header>

          {/* Navigation Bar */}
          <Navigation />

          {/* Main Content */}
          <main className="container mx-auto px-4 py-8 min-h-[60vh]">
            <Routes>
              <Route path="/" element={<HomePage />} />
              
              <Route 
                path="/uyelik" 
                element={<MembershipPage onLogin={triggerLoginState} />} 
              />
              
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/gallery/folders/:id" element={<AlbumDetailPage />} />
              <Route path="/etkinlikler" element={<EventsPage />} />
              <Route path="/duyurular" element={<NewsPage />} />
              <Route path="/duyurular/:id" element={<AnnouncementDetailPage />} />
              <Route path="/galeri" element={<GalleryPage />} />
              <Route path="/iletisim" element={<ContactPage />} />
              <Route path="/mezun-agi" element={<AlumniNetworkPage />} />
              
              {/* Korumalı Rotalar */}
              <Route 
                path="/profil" 
                element={isLoggedIn ? <ProfilePage /> : <Navigate to="/uyelik?tab=giris" replace />} 
              />
              <Route 
                path="/profil-ayarlar" 
                element={isLoggedIn ? <ProfileSettingsPage /> : <Navigate to="/uyelik?tab=giris" replace />} 
              />
              <Route 
                path="/admin" 
                element={isLoggedIn && isAdmin ? <AdminPanelPage /> : <Navigate to="/" replace />} 
              />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>

          {/* Footer */}
          <footer className="bg-gray-800 text-white mt-16">
            <div className="container mx-auto px-4 py-12">
              {/* grid-cols-2 yaparak elemanları geniş ekranda iki eşit sütuna böldük */}
              <div className="grid md:grid-cols-2 gap-8 text-center md:text-left max-w-4xl mx-auto">
                <div className="space-y-2">
                  <h3 className="font-bold text-lg mb-2 text-red-400">SDAL Mezunlar Topluluğu</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    Mezunlarımız arasındaki dayanışmayı güçlendirmek ve bağlarımızı koparmamak için buradayız.
                  </p>
                </div>
                <div className="space-y-2 md:text-right"> {/* Büyük ekranlarda sağa yaslayarak denge sağladık */}
                  <h3 className="font-bold text-lg mb-2 text-red-400">İletişim</h3>
                  <p className="text-slate-400 text-sm">E-posta: <a href="mailto:bilgi@sdal.org.tr" className="hover:underline text-white">bilgi@sdal.org.tr</a></p>
                </div>
              </div>
              
              {/* Alt çizgi ve net ortalanmış telif yazısı */}
              <div className="border-t border-slate-700 mt-8 pt-8 text-center text-slate-500 text-xs w-full">
                &copy; {new Date().getFullYear()} Tüm Hakları Saklıdır.
              </div>
            </div>
          </footer>

          <Toaster />
        </div>
      </Router>
    </GoogleOAuthProvider>
  );
}